"use client";

import { fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import getEnvVariables from "@/libs/shared/api/getEnvVariables";
import type { AppDispatch, RootState } from "@/libs/shared/store";
import { EnvVariablesType } from "@/libs/shared/hooks/useEnvVariables";
import type { BaseQueryFn } from "@reduxjs/toolkit/src/query/baseQueryTypes";

/** 401 on these endpoints does not mean the PT session is invalid (wrong password, signup, etc.). */
const NO_SESSION_CLEAR_ON_401 = new Set([
  "login",
  "register",
  "completeRegistration",
  "recoverPassword",
  "restorePassword",
  "getResetPasswordUserInfo",
  "checkPaymentSucceeded",
]);

const nutritionEndpoints = new Set([
  "getNutritionPlans",
  "getAssignedNutritionPlans",
  "generateToken",
  "getFoods",
  "deleteFoods",
  "deleteNutritionPlan",
  "duplicateNutritionPlan",
  "assignNutritionPlan",
  "toggleNutritionPlan",
]);

type HeaderRule = (
  headers: Headers,
  state: RootState,
  env: EnvVariablesType,
) => void;

const headerRules: Record<string, HeaderRule[]> = {
  register: [(h) => h.set("Auth", "REVOO2019")],
  completeRegistration: [
    (h, state) => {
      h.set("Auth", "REVOO2019");
      const apiToken = state?.auth?.apiToken;
      if (apiToken) h.set("authorization", `Bearer ${apiToken}`);
    },
  ],
  recoverPassword: [(h) => h.set("Auth", "REVOO2019")],
  restorePassword: [(h) => h.set("Auth", "REVOO2019")],
  getResetPasswordUserInfo: [(h) => h.set("Auth", "REVOO2019")],
  checkPaymentSucceeded: [(h) => h.set("Auth", "REVOO2019")],
  getNutritionPlans: [
    (h, state) => {
      const token = state?.auth?.nutritionApiToken;
      if (token) h.set("authorization", `Bearer ${token}`);
    },
  ],
  getAssignedNutritionPlans: [
    (h, state) => {
      const token = state?.auth?.nutritionApiToken;
      if (token) h.set("authorization", `Bearer ${token}`);
    },
  ],
  deleteNutritionPlan: [
    (h, state) => {
      const token = state?.auth?.nutritionApiToken;
      if (token) h.set("authorization", `Bearer ${token}`);
    },
  ],
  duplicateNutritionPlan: [
    (h, state) => {
      const token = state?.auth?.nutritionApiToken;
      if (token) h.set("authorization", `Bearer ${token}`);
    },
  ],
  assignNutritionPlan: [
    (h, state) => {
      const token = state?.auth?.nutritionApiToken;
      if (token) h.set("authorization", `Bearer ${token}`);
    },
  ],
  toggleNutritionPlan: [
    (h, state) => {
      const token = state?.auth?.nutritionApiToken;
      if (token) h.set("authorization", `Bearer ${token}`);
    },
  ],
  getFoods: [
    (h, state) => {
      const token = state?.auth?.nutritionApiToken;
      if (token) h.set("authorization", `Bearer ${token}`);
    },
  ],
  deleteFoods: [
    (h, state) => {
      const token = state?.auth?.nutritionApiToken;
      if (token) h.set("authorization", `Bearer ${token}`);
    },
  ],
  // NOTE: getMessages has been removed - use useCometChatMessages hook instead
  // (server action keeps COMETCHAT_API_KEY secure on server)
};

const baseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const env = await getEnvVariables();
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: nutritionEndpoints.has(api.endpoint)
      ? `${env.NUTRITION_BUILDER_URL}/api`
      : env.API_URL,
    prepareHeaders: (headers, { getState, endpoint, type }) => {
      const state = getState() as any;

      if (type === "query") {
        headers.set("Accept", "application/json");
      }

      const token = state?.auth?.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }

      // applica regole specifiche per endpoint
      headerRules[endpoint]?.forEach((rule) => rule(headers, state, env));

      return headers;
    },
  });

  const result = await rawBaseQuery(args, api, extraOptions);

  const status = (result.error as { status?: number } | undefined)?.status;
  if (
    result.error &&
    status === 401 &&
    !NO_SESSION_CLEAR_ON_401.has(api.endpoint)
  ) {
    const state = api.getState() as RootState;
    if (state.auth?.token) {
      void import("@/libs/auth/sessionLogout").then(({ clearClientSession }) => {
        void clearClientSession(api.dispatch as AppDispatch);
      });
    }
  }

  return result;
};

export default baseQuery;
